import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  Renderer2,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { LocalStorageService } from 'src/services/local-storage.service';

@Component({
    selector: 'app-timer',
    templateUrl: './timer.component.html',
    styleUrls: ['./timer.component.scss'],
    standalone: false
})
export class TimerComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('path', { static: true }) path!: ElementRef<SVGCircleElement>;

  circleSize: number = 150;
  circleRadius: number = this.circleSize / 2;
  strokeWidth: number = this.circleSize / 25;

  animationDuration: number = 60;
  animation!: Animation;
  isRunning: boolean = false;
  remainingTime: number = 15;
  isTimerComplete: boolean = false;
  isLocked: boolean = true;
  lockTimeout: any;
  isTouching: boolean = false;

  constructor(
    private renderer: Renderer2,
    private localStorageService: LocalStorageService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const lastSetTime = this.localStorageService.getTimerValue();
    this.remainingTime =
      lastSetTime !== null ? lastSetTime : this.animationDuration;
    this.animationDuration = this.remainingTime;
    this.setCircleSize(150);
  }

  ngAfterViewInit(): void {
    this.createAnimation();
  }

  createAnimation() {
    const path = this.path.nativeElement;
    const circumference =
      2 * Math.PI * (this.circleRadius - this.strokeWidth / 2);

    this.animation = path.animate(
      [
        { strokeDasharray: `${circumference}, ${circumference}` },
        { strokeDasharray: `0, ${circumference}` },
      ],
      {
        duration: this.animationDuration * 1000,
        fill: 'forwards',
        easing: 'linear',
      }
    );

    this.animation?.pause();
  }

  startAnimation() {
    if (!this.isRunning) {
      this.isRunning = true;
      this.isLocked = true;
      this.animation?.play();
      this.updateRemainingTime();
      if (this.isTimerComplete) {
        this.renderer.removeClass(this.path.nativeElement, 'completed');
        this.isTimerComplete = false;
      }
    }
  }

  stopAnimation() {
    this.isRunning = false;
    this.animation?.pause();
  }

  resetAnimation() {
    this.stopAnimation();
    this.animation?.cancel();
    this.remainingTime = this.animationDuration;
    this.renderer.removeClass(this.path.nativeElement, 'completed');
    this.isTimerComplete = false;
  }

  setDuration(duration: number) {
    this.animationDuration = duration;
    this.resetAnimation();
    this.createAnimation();
    this.localStorageService.setTimerValue(duration);
  }

  updateRemainingTime() {
    if (this.isRunning) {
      const currentTime = Number(this.animation?.currentTime || 0);
      this.remainingTime = Math.ceil(
        this.animationDuration - currentTime / 1000
      );
      if (this.remainingTime > 0) {
        requestAnimationFrame(() => this.updateRemainingTime());
      } else {
        this.resetAnimation();
        this.renderer.addClass(this.path.nativeElement, 'completed');
        this.isTimerComplete = true;
      }
    }
  }

  onDurationChange(event: Event) {
    if (!this.isLocked) {
      const selectElement = event.target as HTMLSelectElement;
      const newDuration = Number(selectElement.value);
      this.setDuration(newDuration);
      this.isLocked = true;
      this.clearLockTimeout();
    }
  }

  setCircleSize(size: number) {
    this.circleSize = size;
    document.documentElement.style.setProperty('--circle-size', `${size}px`);
    this.circleRadius = size / 2;
    this.strokeWidth = size / 25;
    this.resetAnimation();
    this.createAnimation();
  }

  unlockPicker(event: Event) {
    if (this.isLocked) {
      event.stopPropagation();
      this.isLocked = false;
      this.startLockTimer();
    }
  }

  startLockTimer() {
    this.clearLockTimeout();
    this.ngZone.runOutsideAngular(() => {
      this.lockTimeout = setTimeout(() => {
        this.ngZone.run(() => {
          if (!this.isTouching) {
            this.isLocked = true;
          } else {
            this.startLockTimer();
          }
        });
      }, 5000);
    });
  }

  clearLockTimeout() {
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
    }
  }

  onTouchStart() {
    this.isTouching = true;
  }

  onTouchEnd() {
    this.isTouching = false;
    this.startLockTimer();
  }

  ngOnDestroy() {
    this.clearLockTimeout();
  }
}
